import AppRouter from "./components/AppRouter";
import { AuthProvider } from "./context/AuthContext";
import { UserProvider } from "./context/UserContext";
import { ProductProvider } from "./context/ProductContext";
import { AnnouncementProvider } from "./context/AnnouncementContext";
import { TourProvider } from "./context/TourContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer, Slide, type ToastContainerProps } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles/toasts.css";
import { Check, Info, TriangleAlert, X } from "lucide-react";
import Maintenance from "./components/Maintenance";
import PastDueSubscriptionModal from "./components/PastDueSubscriptionModal";
import AnnouncementPopupModal from "./components/AnnouncementPopupModal";
import InvalidStoresModal from "./components/InvalidStoresModal";
import LanguageSync from "./components/LanguageSync";
import AppTour from "./components/AppTour";
import { MaintenanceService } from "./services/MaintenanceService";
import { getTokensFromStorage, saveCredsInLocalStorage } from "./services/AuthService";
import { useEffect, useState } from "react";
import { BillingProvider } from "./context/BillingContext";
import AnonymousChallengeHost from "./components/AnonymousChallengeHost";
import useViewportOffsetReset from "./hooks/useViewportOffsetReset";
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Handle access_token from email setup links (not OAuth callbacks)
function handleAccessTokenFromUrl() {
  // Skip on OAuth callback routes - they handle tokens themselves
  if (window.location.pathname.startsWith("/auth/")) return false;

  const urlParams = new URLSearchParams(window.location.search);
  const accessToken = urlParams.get("access_token");
  
  if (accessToken) {
    saveCredsInLocalStorage(accessToken, accessToken);
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete("access_token");
    window.history.replaceState({}, "", newUrl.toString());
    window.location.reload();
    return true;
  }
  return false;
}

function App() {
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // iOS browsers can leave the non-scrolling document overscrolled on first
  // paint, which floats the fixed bottom nav mid-screen; see the hook.
  useViewportOffsetReset();

  useEffect(() => {
    // Check for access_token in URL first
    if (handleAccessTokenFromUrl()) {
      return; // Will reload the page
    }

    const checkMaintenance = async () => {
      const tokens = getTokensFromStorage();
      if (tokens) {
        const underMaintenance = await MaintenanceService.checkMaintenance();
        setIsUnderMaintenance(underMaintenance);
      }
      setIsLoading(false);
    };

    checkMaintenance();
  }, []);

  if (isLoading) {
    return null;
  }

  if (isUnderMaintenance) {
    return <Maintenance />;
  }

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <UserProvider>
            <BillingProvider>
              <AnnouncementProvider>
                <ProductProvider>
                  {/* The tour stays manual (Filters → help); first visits get the deal-card guide instead. */}
                  <TourProvider autoStart={false}>
                    <AppRouter />
                    <AnonymousChallengeHost />
                    <ThemedToastContainer />
                    <LanguageSync />
                    <PastDueSubscriptionModal />
                    <InvalidStoresModal />
                    <AnnouncementPopupModal />
                    <AppTour />
                  </TourProvider>
                </ProductProvider>
              </AnnouncementProvider>
            </BillingProvider>
          </UserProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

const toastIcon: ToastContainerProps["icon"] = ({ type }) => {
  if (type === "success") return <Check aria-hidden="true" />;
  if (type === "error") return <X aria-hidden="true" />;
  if (type === "warning") return <TriangleAlert aria-hidden="true" />;
  return <Info aria-hidden="true" />;
};

const toastCloseButton: ToastContainerProps["closeButton"] = ({ closeToast }) => (
  <button type="button" className="dh-toast-close" aria-label="Dismiss" onClick={closeToast}>
    <X size={15} />
  </button>
);

// Styled as white cards that slide in from the top; see styles/toasts.css.
const PHONE_TOAST_QUERY = "(max-width: 767px)";

function ThemedToastContainer() {
  const { isDark } = useTheme();
  // Phones slide the card down from the top center; desktop slides it in at the top right.
  const [isPhone, setIsPhone] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(PHONE_TOAST_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia?.(PHONE_TOAST_QUERY);
    if (!query) return;
    const update = () => setIsPhone(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return (
    <ToastContainer
      className="dh-toasts"
      position={isPhone ? "top-center" : "top-right"}
      autoClose={3000}
      hideProgressBar
      closeButton={toastCloseButton}
      newestOnTop
      limit={3}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      transition={Slide}
      icon={toastIcon}
      theme={isDark ? "dark" : "light"}
    />
  );
}

export default App;
